import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

const RESERVATION_KEY = 'velune_cart_reservation_deadline_v1';
const RESERVATION_MS = 20 * 60 * 1000;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Promise<unknown> | null} */
  #veluneCartModule = null;

  #reservationDeadline = 0;

  #reservationInterval = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRendered);
    this.addEventListener(ThemeEvents.cartSectionUpdated, this.#handleCartSectionRendered);
    document.addEventListener('visibilitychange', this.#handleReservationVisibility);
    this.#syncReservation(!this.#isCartEmpty());

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRendered);
    this.removeEventListener(ThemeEvents.cartSectionUpdated, this.#handleCartSectionRendered);
    document.removeEventListener('visibilitychange', this.#handleReservationVisibility);
    this.#stopReservationInterval();
  }

  /**
   * Loads Velune-only cart behavior after the cart is actually used.
   * No extra cart bundle is requested during the initial page load.
   */
  #loadVeluneCart() {
    if (this.#veluneCartModule) return;
    this.#veluneCartModule = import('./velune-cart.js').catch((error) => {
      this.#veluneCartModule = null;
      console.warn('[cart-drawer] Velune cart module failed to load:', error);
    });
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRendered = () => {
    this.#syncReservation(!this.#isCartEmpty());
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    if (!this.#isCartEmpty()) this.#loadVeluneCart();
    this.#updateStickyState();
    if (!this.#isCartEmpty()) this.#syncReservation(true);

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action === 'add') this.#loadVeluneCart();

    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.didError) {
          const itemCount = Number(detail?.itemCount);
          if (Number.isFinite(itemCount)) this.#syncReservation(itemCount > 0);
          else requestAnimationFrame(() => this.#syncReservation(!this.#isCartEmpty()));
        }
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #stopReservationInterval() {
    if (!this.#reservationInterval) return;
    window.clearInterval(this.#reservationInterval);
    this.#reservationInterval = 0;
  }

  #renderReservation() {
    const display = this.querySelector('[data-velune-reservation-time]');
    if (!display || !this.#reservationDeadline) return;
    const seconds = Math.max(0, Math.ceil((this.#reservationDeadline - Date.now()) / 1000));
    const value = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' +
      String(seconds % 60).padStart(2, '0');
    if (display.textContent !== value) display.textContent = value;
    if (seconds === 0 || !(this.#themeDrawer?.isOpen || this.#themeDrawer?.hasAttribute('open'))) {
      this.#stopReservationInterval();
    }
  }

  #syncReservation(hasItems) {
    if (!hasItems) {
      this.#reservationDeadline = 0;
      this.#stopReservationInterval();
      try { localStorage.removeItem(RESERVATION_KEY); } catch {}
      return;
    }

    if (!this.#reservationDeadline) {
      try {
        const stored = Number(localStorage.getItem(RESERVATION_KEY));
        if (Number.isFinite(stored) && stored > 0) this.#reservationDeadline = stored;
      } catch {}
      if (!this.#reservationDeadline) {
        this.#reservationDeadline = Date.now() + RESERVATION_MS;
        try { localStorage.setItem(RESERVATION_KEY, String(this.#reservationDeadline)); } catch {}
      }
    }

    this.#renderReservation();
    if (!document.hidden && (this.#themeDrawer?.isOpen || this.#themeDrawer?.hasAttribute('open')) &&
        !this.#reservationInterval && this.#reservationDeadline > Date.now()) {
      this.#reservationInterval = window.setInterval(() => this.#renderReservation(), 1000);
    }
  }

  #handleReservationVisibility = () => {
    if (document.hidden) this.#stopReservationInterval();
    else if (!this.#isCartEmpty()) this.#syncReservation(true);
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
